

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import TopBar from "./topbar";
import Footer from "./footer";

const PostDetails = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [responses, setResponses] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [likes, setLikes] = useState(0);
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const userData = JSON.parse(localStorage.getItem("user"));
  const commenterId = userData?.id;

  // -------------------------
  // Fetch Post
  // -------------------------
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await fetch(`https://backend-global-1vq9.onrender.com/api/posts/${id}`);
        if (!res.ok) throw new Error("Failed to load post");
        const data = await res.json();
        setPost(data);
        setLikes(data.likes || 0);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);

  // -------------------------
  // Fetch Post Responses
  // -------------------------
  const fetchResponses = async () => {
    try {
      const res = await fetch(`https://backend-global-1vq9.onrender.com/api/responses/${id}`);
      const data = await res.json();
      setResponses(data);
    } catch (err) {
      console.log("Error loading responses", err);
    }
  };

  useEffect(() => {
    fetchResponses();
  }, [id]);

  // -------------------------
  // Handle Add Like
  // -------------------------
  const handleLike = async () => {
    setLikes(likes + 1);
    try {
      await fetch(`https://backend-global-1vq9.onrender.com/api/posts/${id}/like`, {
        method: "POST",
      });
    } catch (err) {
      console.log("Like failed", err);
      setLikes(likes);
    }
  };

  // -------------------------
  // Handle Add Response
  // -------------------------
  const handleSubmitResponse = async () => {
    if (!newMessage.trim()) return;

    const payload = {
      postId: id,
      message: newMessage,
      createdBy: commenterId,
    };

    try {
      await fetch(`https://backend-global-1vq9.onrender.com/api/responses`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      setNewMessage("");
      fetchResponses();
    } catch (err) {
      console.log("Error submitting response", err);
    }
  };

  // ================================
  // UI Rendering
  // ================================
  if (loading)
    return (
      <Box textAlign="center" mt={10}>
        <CircularProgress sx={{ color: "primary.main" }} />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" textAlign="center" mt={10}>
        {error}
      </Typography>
    );

  return (
    <Box>
      {/* TOP BAR */}
      <TopBar />
      <Box sx={{ p: { xs: 2, sm: 3, md: 5 }, pb: 0, display: "flex", justifyContent: "center", bgcolor: "background.default" }}>
        <Box sx={{ width: { xs: "100%", sm: "90%", md: "70%" } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "primary.main",
              fontWeight: 600,
              textTransform: "none",
              mb: 1,
              "&:hover": { bgcolor: "rgba(10, 46, 138, 0.05)" },
            }}
          >
            Back to Community Posts
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 5 },
          display: "flex",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Card
          sx={{
            width: { xs: "100%", sm: "90%", md: "70%" },
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid rgba(2, 8, 23, 0.06)",
            transition: "all 0.3s ease",
            "&:hover": {
              borderColor: "rgba(10, 46, 138, 0.2)",
              boxShadow: "0 12px 32px rgba(10, 46, 138, 0.1)",
            },
          }}
        >
          {/* Post Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            mb={2}
            sx={{ fontSize: { xs: "1.3rem", sm: "1.8rem", md: "2.125rem" }, color: "primary.main" }}
          >
            {post.title}
          </Typography>

          {/* Meta info */}
          <Typography color="text.secondary" mb={1} sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
            Category: <strong style={{ color: "#0A2E8A" }}>{post.category}</strong>
          </Typography>

          <Typography color="text.secondary" mb={1} sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
            Posted by: <strong style={{ color: "#0A2E8A" }}>{post.createdBy?.email}</strong>
          </Typography>

          <Typography color="text.secondary" mb={2} sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
            Date: {new Date(post.createdAt).toLocaleString()}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          {/* Image / Video */}
          {post.mediaType === "image" && post.mediaUrl && (
            <img
              src={post.mediaUrl}
              alt={post.title}
              style={{
                width: "100%",
                maxHeight: isMobile ? 220 : 400,
                objectFit: "cover",
                borderRadius: 10,
                marginBottom: 20,
                display: "block",
              }}
            />
          )}

          {post.mediaType === "video" && post.mediaUrl && (
            <video
              src={post.mediaUrl}
              controls
              style={{
                width: "100%",
                borderRadius: 10,
                marginBottom: 20,
                display: "block",
              }}
            />
          )}

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              whiteSpace: "pre-line",
              fontSize: { xs: "0.875rem", sm: "1rem" },
              lineHeight: 1.7,
            }}
          >
            {post.description}
          </Typography>

          {/* Like + Comment Bar */}
          <Box display="flex" alignItems="center" mt={3} gap={2}>
            <IconButton onClick={handleLike} color="error" size={isMobile ? "small" : "medium"}>
              <FavoriteIcon />
            </IconButton>
            <Typography variant={isMobile ? "body2" : "body1"}>{likes}</Typography>

            <ChatBubbleOutlineIcon sx={{ fontSize: isMobile ? 18 : 24, color: "primary.main" }} />
            <Typography variant={isMobile ? "body2" : "body1"}>{responses.length}</Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* COMMENT INPUT */}
          <Typography
            variant="h6"
            mb={1}
            sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main", fontWeight: 700 }}
          >
            Add a Comment
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={isMobile ? 2 : 3}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Write your comment..."
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#0A2E8A",
                },
              },
            }}
          />

          <Button
            variant="contained"
            onClick={handleSubmitResponse}
            size={isMobile ? "small" : "medium"}
            fullWidth={isMobile}
            sx={{
              background: "linear-gradient(135deg, #0A2E8A 0%, #0090D9 100%)",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: 2,
            }}
          >
            Submit Comment
          </Button>

          {/* COMMENT LIST */}
          <Box mt={4}>
            <Typography
              variant="h6"
              mb={2}
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main", fontWeight: 700 }}
            >
              Comments
            </Typography>

            {responses.length === 0 ? (
              <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary">
                No comments yet.
              </Typography>
            ) : (
              responses.map((r) => (
                <Card
                  key={r._id}
                  sx={{
                    p: { xs: 1.5, sm: 2 },
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(10, 46, 138, 0.05)",
                    border: "none",
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, color: "primary.main" }}
                  >
                    {r.createdBy?.email}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                    {r.message}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                    {new Date(r.createdAt).toLocaleString()}
                  </Typography>
                </Card>
              ))
            )}
          </Box>
        </Card>
      </Box>

      {/* FOOTER */}
      <Footer />
    </Box>
  );
};

export default PostDetails;